/**
 * FILE: packages\supabase\src\services\websitePaymentSync.ts
 * PURPOSE: Supabase service layer — database access and business logic.
 * AGENT_CONTEXT: All DB access goes through service layer. Call logActivity() on writes.
 * INVARIANTS: Soft-delete filter, phone PK protection, activity logging.
 * LAST_REVIEWED: 2026-05-07
 */


import { logActivity, type ActivityActor, withActivityActor } from '../internal/activity'
import type { Database } from '../types'
import type { DbClient } from "../dbClient";
import { defaultDbClient } from "../dbClient";
import type { EdgeFunctionInvoker } from "../edgeFunctions";
import { defaultEdgeFunctionInvoker } from "../edgeFunctions";

type SyncLogRow = Database['public']['Tables']['website_payment_sync_log']['Row']
type IntegrationSettingsRow = Database['public']['Tables']['integration_settings']['Row']

export type WebsiteSyncLogStatus = SyncLogRow['status']

export type IntegrationSettings = IntegrationSettingsRow

const SETTINGS_ROW_ID = 'default'

export async function getIntegrationSettings(client: DbClient = defaultDbClient): Promise<IntegrationSettings> {
  const { data, error } = await client
    .from('integration_settings')
    .select('*')
    .eq('id', SETTINGS_ROW_ID)
    .maybeSingle()

  if (error) throw error
  if (data) return data

  return {
    id: SETTINGS_ROW_ID,
    payment_sync_enabled: false,
    payment_sync_dry_run: true,
    onboarding_email_enabled: false,
    existing_student_portal_email_enabled: false,
    student_update_email_enabled: false,
    catalog_sync_enabled: true,
    website_sync_suppress_comms: true,
    catalog_full_refresh_every_n_runs: 10,
    email_sending_enabled: null,
    payment_sync_live_at: null,
    backfill_started_at: null,
    installment_reminder_email_enabled: false,
    updated_at: new Date().toISOString(),
  }
}

export async function updateIntegrationSettings(
  patch: Partial<
    Pick<
      IntegrationSettings,
      | 'payment_sync_enabled'
      | 'payment_sync_dry_run'
      | 'onboarding_email_enabled'
      | 'catalog_sync_enabled'
      | 'website_sync_suppress_comms'
      | 'catalog_full_refresh_every_n_runs'
      | 'email_sending_enabled'
      | 'payment_sync_live_at'
      | 'backfill_started_at'
    >
  >,
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<IntegrationSettings> {
  const { data, error } = await client
    .from('integration_settings')
    .update(patch)
    .eq('id', SETTINGS_ROW_ID)
    .select()
    .single()

  if (error) throw error

  await logActivity(
    withActivityActor(actor, {
      action: 'UPDATE_INTEGRATION_SETTINGS',
      entityType: 'integration_settings',
      entityId: SETTINGS_ROW_ID,
      newValues: patch,
    }), client
  )

  return data
}

export interface WebsiteSyncLogFilters {
  status?: WebsiteSyncLogStatus | WebsiteSyncLogStatus[]
  sanityId?: string
  search?: string
  sinceProcessedAt?: string
  limit?: number
  offset?: number
}

export type WebsiteSyncPollCursorKey =
  | 'website_payments_last_updated_at'
  | 'website_catalog_last_synced_at'

export type WebsiteSyncWorkerAction =
  | 'run_catalog'
  | 'run_payments'
  | 'run_reconcile'
  | 'run_payments_until_idle'
  | 'run_full_reconcile'
  | 'run_international_audit'

export type WebsiteSyncPipelineMode = 'off' | 'shadow' | 'live'

export interface WebsiteSyncEdgeHealthResponse {
  healthy: boolean
  pipeline_mode: WebsiteSyncPipelineMode
  payment_sync_enabled: boolean
  catalog_sync_enabled: boolean
  website_db: { ok: boolean; latency_ms: number }
  cursors: { payments: string; catalog: string }
  sync_lag_seconds: number | null
  catalog_lag_seconds: number | null
  last_catalog_sync_run: { status: string; rows_upserted: number; completed_at: string } | null
  checked_at: string
}

export interface WebsiteSyncCronError {
  id: string
  function_name: string
  error_message: string
  context: unknown
  created_at: string | null
}

export interface WebsiteSyncHealth {
  integration_settings?: Record<string, unknown>
  cursors?: Record<string, unknown>
  catalog?: Record<string, unknown>
  sync_log?: Record<string, number>
  needs_review_count?: number
  failed_last_24h?: number
  synced_last_24h?: number
  status_counts_24h?: Record<string, number>
  pipeline_mode?: WebsiteSyncPipelineMode
  cron_errors_recent?: WebsiteSyncCronError[]
}

export interface WebsiteSyncWorkerResult {
  action: WebsiteSyncWorkerAction
  function: string
  status: number
  ok: boolean
  result: unknown
  error?: string
}

export async function listWebsitePaymentSyncLog(
  filters: WebsiteSyncLogFilters = {}, client: DbClient = defaultDbClient
): Promise<{ data: SyncLogRow[]; count: number }> {
  const limit = filters.limit ?? 50
  const offset = filters.offset ?? 0

  let query = client
    .from('website_payment_sync_log')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1)

  if (filters.status) {
    const statuses = Array.isArray(filters.status) ? filters.status : [filters.status]
    query = query.in('status', statuses)
  }

  if (filters.sanityId) query = query.eq('sanity_id', filters.sanityId)

  if (filters.sinceProcessedAt) {
    query = query.gte('processed_at', filters.sinceProcessedAt)
  }

  if (filters.search?.trim()) {
    const term = filters.search.trim()
    query = query.or(
      `order_id.ilike.%${term}%,phone.ilike.%${term}%,student_phone.ilike.%${term}%,sanity_id.ilike.%${term}%`
    )
  }

  const { data, error, count } = await query
  if (error) throw error

  return { data: data ?? [], count: count ?? 0 }
}

export async function getWebsiteSyncHealth(client: DbClient = defaultDbClient): Promise<WebsiteSyncHealth> {
  const { data, error } = await client.rpc('get_website_sync_health')
  if (error) throw error
  return (data as WebsiteSyncHealth) ?? {}
}

export async function getWebsiteSyncDashboard(client: DbClient = defaultDbClient): Promise<WebsiteSyncHealth> {
  const { data, error } = await client.rpc('get_website_sync_dashboard')
  if (error) throw error
  return (data as WebsiteSyncHealth) ?? {}
}

export async function setWebsiteSyncPollCursor(
  key: WebsiteSyncPollCursorKey,
  ts: string | null,
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<string> {
  const { data, error } = await client.rpc('set_website_sync_poll_cursor', {
    p_key: key,
    p_ts: ts ?? new Date().toISOString(),
  })
  if (error) throw error

  await logActivity(
    withActivityActor(actor, {
      action: 'SET_WEBSITE_SYNC_POLL_CURSOR',
      entityType: 'poll_state',
      entityId: key,
      newValues: { last_polled_at: data },
    }), client
  )

  return data as string
}

const WEBSITE_SYNC_FUNCTIONS = [
  'sync-website-catalog',
  'sync-website-payments',
  'reconcile-website-payments',
] as const

export async function listWebsiteSyncCronErrors(
  limit = 20, client: DbClient = defaultDbClient
): Promise<WebsiteSyncCronError[]> {
  const { data, error } = await client
    .from('cron_errors')
    .select('id, function_name, error_message, context, created_at')
    .in('function_name', [...WEBSITE_SYNC_FUNCTIONS])
    .order('created_at', { ascending: false })
    .limit(limit)

  if (error) throw error
  return (data ?? []) as WebsiteSyncCronError[]
}

export async function triggerWebsiteSyncWorker(
  action: WebsiteSyncWorkerAction,
  _actor: ActivityActor, invoke: EdgeFunctionInvoker = defaultEdgeFunctionInvoker
): Promise<WebsiteSyncWorkerResult> {
  const { data, error } = await invoke('website-sync-admin', {
    body: { action },
  })

  if (error) {
    return {
      action,
      function: '',
      status: 500,
      ok: false,
      result: null,
      error: error.message,
    }
  }

  const payload = data as WebsiteSyncWorkerResult | null
  return {
    action: payload?.action ?? action,
    function: payload?.function ?? '',
    status: payload?.status ?? 200,
    ok: payload?.ok ?? true,
    result: payload?.result ?? data,
    error: (payload as { error?: string } | null)?.error,
  }
}

/** Reset a failed/needs_review row for the next sync worker pass. */
export async function retryWebsitePaymentSync(
  syncLogId: string,
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<SyncLogRow> {
  const { data: existing, error: readErr } = await client
    .from('website_payment_sync_log')
    .select('*')
    .eq('id', syncLogId)
    .single()

  if (readErr) throw readErr
  if (!existing) throw new Error('Sync log row not found')

  const retriable = ['failed', 'needs_review']
  if (!retriable.includes(existing.status)) {
    throw new Error(`Cannot retry status ${existing.status}`)
  }

  const { data, error } = await client
    .from('website_payment_sync_log')
    .update({
      status: 'pending',
      error_code: null,
      error_message: null,
      processed_at: null,
      retry_count: (existing.retry_count ?? 0) + 1,
    })
    .eq('id', syncLogId)
    .select()
    .single()

  if (error) throw error

  await logActivity(
    withActivityActor(actor, {
      action: 'RETRY_WEBSITE_PAYMENT_SYNC',
      entityType: 'website_payment_sync_log',
      entityId: syncLogId,
      newValues: { website_payment_intent_id: existing.website_payment_intent_id },
    }), client
  )

  return data
}

/** Mark a needs_review row as staff-reviewed (keeps audit trail). */
export async function markWebsitePaymentReviewed(
  syncLogId: string,
  note: string,
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<SyncLogRow> {
  const stamp = new Date().toISOString()
  const suffix = `[${stamp.slice(0, 10)}] reviewed by ${actor.userName}: ${note.trim()}; `

  const { data: existing, error: readErr } = await client
    .from('website_payment_sync_log')
    .select('*')
    .eq('id', syncLogId)
    .single()

  if (readErr) throw readErr
  if (!existing) throw new Error('Sync log row not found')

  const { data, error } = await client
    .from('website_payment_sync_log')
    .update({
      status: 'reviewed',
      error_code: 'staff_reviewed',
      error_message: `${existing.error_message ?? ''}${suffix}`.trim(),
      processed_at: stamp,
    })
    .eq('id', syncLogId)
    .select()
    .single()

  if (error) throw error

  if (existing.student_phone) {
    await client
      .from('students')
      .update({ needs_review: false })
      .eq('phone', existing.student_phone)
  }

  await logActivity(
    withActivityActor(actor, {
      action: 'MARK_WEBSITE_PAYMENT_REVIEWED',
      entityType: 'website_payment_sync_log',
      entityId: syncLogId,
      newValues: { note },
    }), client
  )

  return data
}

export interface WebsiteReconciliationGaps {
  generated_at?: string
  summary?: {
    synced_total?: number
    live_since_go_live?: number
    backfill_window?: number
    missing_in_hub?: number
    in_hub_no_enrollment?: number
    duplicate_enrollment?: number
    attribute_drift?: number
    already_correct?: number
    website_missing_in_hub?: number
    website_already_correct?: number
  }
  attribute_drift_rows?: unknown[]
  latest_drift_rows?: unknown[]
  open_gaps?: unknown[]
  website_gaps?: unknown[]
}

export interface WebsiteSyncActivityLogRow {
  id: string
  processed_at: string | null
  created_at: string
  sync_action: string | null
  status: string
  phone: string | null
  order_id: string | null
  sanity_id: string | null
  website_payment_intent_id: string
  course: string | null
  program: string | null
  attempt: string | null
  valid_till: string | null
  amount: string | null
  currency: string | null
  country_code: string | null
  country: string | null
  enrollment_id: string | null
  is_live_capture: boolean
  derived_fields?: unknown
}

export interface WebsiteSyncActivityLogFilters {
  since?: 'all' | 'backfill' | 'go_live'
  status?: WebsiteSyncLogStatus | 'all'
  action?: string | 'all'
  search?: string
  limit?: number
  offset?: number
}

export interface WebsiteSyncActivityLogResult {
  rows: WebsiteSyncActivityLogRow[]
  total_count: number
}

export interface WebsiteFullReconcileResult {
  status?: string
  mode?: string
  checked_at?: string
  summary?: {
    website_total?: number
    missing_in_hub?: number
    already_correct?: number
    attribute_drift?: number
  }
  website_gaps?: unknown[]
  error?: string
}

export interface WebsiteSyncDailyReport {
  since?: string
  payment_sync_live_at?: string | null
  backfill_started_at?: string | null
  website_sync_suppress_comms?: boolean
  status_counts?: Record<string, number>
  error_counts?: Record<string, number>
  synced_count?: number
  needs_review_count?: number
}

export async function getWebsiteReconciliationGaps(client: DbClient = defaultDbClient): Promise<WebsiteReconciliationGaps> {
  const { data, error } = await client.rpc('get_website_reconciliation_gaps')
  if (error) throw error
  return (data as WebsiteReconciliationGaps) ?? {}
}

export async function listWebsiteSyncActivityLog(
  filters: WebsiteSyncActivityLogFilters = {}, client: DbClient = defaultDbClient
): Promise<WebsiteSyncActivityLogResult> {
  const { data, error } = await client.rpc('get_website_sync_activity_log', {
    p_since: filters.since ?? 'all',
    p_status: filters.status === 'all' || !filters.status ? undefined : filters.status,
    p_action: filters.action === 'all' || !filters.action ? undefined : filters.action,
    p_search: filters.search?.trim() || undefined,
    p_limit: filters.limit ?? 25,
    p_offset: filters.offset ?? 0,
  })
  if (error) throw error

  const payload = (data as WebsiteSyncActivityLogResult | null) ?? { rows: [], total_count: 0 }
  return {
    rows: payload.rows ?? [],
    total_count: payload.total_count ?? 0,
  }
}

export async function runWebsiteFullReconcile(
  actor: ActivityActor
): Promise<WebsiteFullReconcileResult> {
  const worker = await triggerWebsiteSyncWorker('run_full_reconcile', actor)
  if (!worker.ok) {
    return { error: worker.error ?? `Full reconcile failed (${worker.status})` }
  }
  return (worker.result as WebsiteFullReconcileResult) ?? {}
}

export async function getWebsiteSyncDailyReport(client: DbClient = defaultDbClient): Promise<WebsiteSyncDailyReport> {
  const { data, error } = await client.rpc('get_website_sync_daily_report')
  if (error) throw error
  return (data as WebsiteSyncDailyReport) ?? {}
}

/** Requeue all dry_run_ok / needs_review / failed rows for backfill reprocessing. */
export async function requeueWebsitePaymentSyncForBackfill(
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<{ updated: number }> {
  const { data, error } = await client
    .from('website_payment_sync_log')
    .update({
      status: 'pending',
      error_code: null,
      error_message: null,
      processed_at: null,
    })
    .in('status', ['dry_run_ok', 'needs_review', 'failed'])
    .select('id')

  if (error) throw error

  const updated = data?.length ?? 0

  await logActivity(
    withActivityActor(actor, {
      action: 'REQUEUE_WEBSITE_PAYMENT_SYNC_BACKFILL',
      entityType: 'website_payment_sync_log',
      entityId: 'batch',
      newValues: { requeued: updated },
    }), client
  )

  return { updated }
}

/** Reset payments cursor to epoch for full historical backfill. */
export async function resetWebsitePaymentsCursorForBackfill(
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<string> {
  const ts = await setWebsiteSyncPollCursor('website_payments_last_updated_at', '1970-01-01T00:00:00Z', actor, client)

  await client
    .from('integration_settings')
    .update({
      backfill_started_at: new Date().toISOString(),
      payment_sync_dry_run: false,
      website_sync_suppress_comms: true,
      onboarding_email_enabled: false,
    })
    .eq('id', SETTINGS_ROW_ID)

  return ts
}

export async function getWebsitePaymentSyncLogById(syncLogId: string, client: DbClient = defaultDbClient): Promise<SyncLogRow | null> {
  const { data, error } = await client
    .from('website_payment_sync_log')
    .select('*')
    .eq('id', syncLogId)
    .maybeSingle()

  if (error) throw error
  return data
}

export interface InternationalAuditSummary {
  website_enrollments_total?: number
  missing_enrollment_currency?: number
  usd_students?: number
  null_country_code_students?: number
  invalid_phone_sync_rows?: number
  synced_with_payload_currency?: number
}

export interface InternationalAuditResult {
  checked_at?: string
  total?: number
  summary?: Record<string, number>
  rows?: Array<Record<string, unknown>>
  error?: string
}

export async function getInternationalDataAuditSummary(client: DbClient = defaultDbClient): Promise<InternationalAuditSummary> {
  const { data, error } = await client.rpc('get_international_data_audit_summary')
  if (error) throw error
  return (data as InternationalAuditSummary) ?? {}
}

export async function runInternationalAudit(actor: ActivityActor): Promise<InternationalAuditResult> {
  const worker = await triggerWebsiteSyncWorker('run_international_audit', actor)
  if (!worker.ok) {
    return { error: worker.error ?? `International audit failed (${worker.status})` }
  }
  return (worker.result as InternationalAuditResult) ?? {}
}

export async function backfillEnrollmentCurrencyFromSyncLog(
  dryRun: boolean, client: DbClient = defaultDbClient
): Promise<Record<string, unknown>> {
  const { data, error } = await client.rpc('backfill_enrollment_currency_from_sync_log', {
    dry_run: dryRun,
  })
  if (error) throw error
  return (data as Record<string, unknown>) ?? {}
}
